import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button, Select } from '../../../custom';
import Input from '../../../custom/input/input';
import { createRole } from '../request';
import { StatusOptions } from '../../../requests';

interface Props {
  handleClose: () => void;
  refetch: () => void;
}

const AddRoleForm = ({ handleClose, refetch }: Props) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const validate = Yup.object().shape({
    name: Yup.string().required('Role name is required'),
    status: Yup.string().required('Status is required'),
  });

  const addRoleMutation = useMutation({ mutationFn: createRole });

  const handleSubmit = async (values: FormikValues, resetForm: () => void) => {
    try {
      await addRoleMutation.mutateAsync(
        {
          id: '',
          name: values.name,
          normalizedName: values.name.toUpperCase(),
          isActive: values.status === 'true',
        },
        {
          onSuccess: (data) => {
            notification.success({
              message: 'Success',
              description: data?.message || 'Role created successfully',
            });
            queryClient.refetchQueries({ queryKey: ['get-all-roles'] });
            refetch();
            resetForm();
            handleClose();
          },
        },
      );
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to create role',
      });
    }
  };

  return (
    <Formik
      initialValues={{ name: '', status: '' }}
      validationSchema={validate}
      onSubmit={(values, { resetForm }) => handleSubmit(values, resetForm)}
      enableReinitialize
    >
      {() => (
        <Form className="fields">
          <Input label="Role Name" placeholder="Input role name" name="name" />

          <Select
            name="status"
            placeholder="Select Status"
            label="Status"
            options={
              <>
                {StatusOptions.map((option: any) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </>
            }
          />

          <div className="btn-group">
            <Button onClick={handleClose} variant="text" text="Cancel" />
            <Button
              text={addRoleMutation.isPending ? 'Submitting...' : 'Submit'}
              type="submit"
              isLoading={addRoleMutation.isPending}
              disabled={addRoleMutation.isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AddRoleForm;
